import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import Breadcrumb from '../../components/ui/Breadcrumb';
import toast from 'react-hot-toast';

const fmtMXN = (n) =>
  `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const pctColor = (pct) => {
  if (pct === null) return 'text-muted';
  return pct >= 0 ? 'text-success' : 'text-danger';
};

const pctArrow = (pct) => {
  if (pct === null) return '';
  return pct >= 0 ? '▲' : '▼';
};

const roleLabels = { notary: 'Notario', supervisor: 'Supervisor', staff: 'Staff' };
const roleColors = { notary: 'badge-primary', supervisor: 'badge-info', staff: 'badge-secondary' };

const appointmentStatusColors = {
  programada: '#3b82f6', confirmada: '#10b981',
  realizada: '#6b7280', cancelada: '#ef4444', reprogramada: '#f59e0b',
};

// Mini bar chart con divs — sin librería externa
const MiniBarChart = ({ data, valueKey, labelKey, color = '#5066E1' }) => {
  if (!data?.length) return <div className="text-muted small text-center py-3">Sin datos</div>;
  const max = Math.max(...data.map(d => d[valueKey])) || 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 60 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div
            title={`${d[labelKey]}: ${fmtMXN(d[valueKey])}`}
            style={{
              width: '100%',
              height: `${Math.max(4, (d[valueKey] / max) * 52)}px`,
              backgroundColor: color,
              borderRadius: '3px 3px 0 0',
              opacity: i === data.length - 1 ? 1 : 0.5,
              transition: 'height 0.3s',
            }}
          />
        </div>
      ))}
    </div>
  );
};

const NotaryDashboard = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/notary')
      .then(res => setData(res.data))
      .catch(() => toast.error('Error al cargar dashboard ejecutivo'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="text-center py-5">
      <div className="spinner-border text-primary"></div>
    </div>
  );

  if (!data) return null;

  const { revenue, case_files, procedure_stats, workload, week_appointments, stale_days } = data;
  const maxWorkload = Math.max(...(workload?.map(u => u.active) ?? [1]), 1);

  return (
    <div className="container-fluid">
      <Breadcrumb items={[{ label: 'Dashboard Ejecutivo' }]} />

      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">Dashboard Ejecutivo</h4>
          <small className="text-muted">Vista gerencial — {new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}</small>
        </div>
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="ti-arrow-left mr-1"></i> Dashboard general
        </Link>
      </div>

      {/* ── Fila 1: KPIs principales ── */}
      <div className="row mb-4">

        {/* Ingresos este mes */}
        <div className="col-12 col-md-3 mb-3">
          <div className="card h-100" style={{ borderLeft: '4px solid #10B981' }}>
            <div className="card-body">
              <div className="text-muted small mb-1">Cobrado este mes</div>
              <h4 className="mb-0 text-success">{fmtMXN(revenue.this_month)}</h4>
              <div className="mt-1">
                {revenue.change_pct !== null ? (
                  <small className={pctColor(revenue.change_pct)}>
                    {pctArrow(revenue.change_pct)} {Math.abs(revenue.change_pct)}% vs mes anterior ({fmtMXN(revenue.last_month)})
                  </small>
                ) : (
                  <small className="text-muted">Sin datos mes anterior</small>
                )}
              </div>
              <div className="mt-3">
                <MiniBarChart
                  data={revenue.weekly}
                  valueKey="total"
                  labelKey="week"
                  color="#10B981"
                />
                <div className="text-muted" style={{ fontSize: '10px', textAlign: 'right', marginTop: 2 }}>
                  Últimas 8 semanas
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Saldo por cobrar */}
        <div className="col-12 col-md-3 mb-3">
          <div className="card h-100" style={{ borderLeft: '4px solid #F59E0B' }}>
            <div className="card-body">
              <div className="text-muted small mb-1">Por cobrar (saldo pendiente)</div>
              <h4 className="mb-0 text-warning">{fmtMXN(revenue.pending)}</h4>
              <div className="mt-2">
                <Link to="/accounts-receivable" className="btn btn-sm btn-outline-warning btn-block mt-2">
                  Ver cuentas por cobrar
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Expedientes activos este mes */}
        <div className="col-12 col-md-3 mb-3">
          <div className="card h-100" style={{ borderLeft: '4px solid #5066E1' }}>
            <div className="card-body">
              <div className="text-muted small mb-1">Expedientes activos este mes</div>
              <h4 className="mb-0" style={{ color: '#5066E1' }}>{case_files.active_this_month}</h4>
              <small className="text-muted">
                {case_files.completed_this_month} cerrado{case_files.completed_this_month !== 1 ? 's' : ''} este mes
              </small>
              <div className="mt-3">
                <MiniBarChart
                  data={case_files.monthly_trend}
                  valueKey="opened"
                  labelKey="label"
                  color="#5066E1"
                />
                <div className="text-muted" style={{ fontSize: '10px', textAlign: 'right', marginTop: 2 }}>
                  Aperturados · últimos 6 meses
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trámites completados este mes */}
        <div className="col-12 col-md-3 mb-3">
          <div className="card h-100" style={{ borderLeft: '4px solid #7C3AED' }}>
            <div className="card-body">
              <div className="text-muted small mb-1">Trámites cerrados este mes</div>
              <h4 className="mb-0" style={{ color: '#7C3AED' }}>{case_files.completed_this_month}</h4>
              <div className="mt-3">
                <MiniBarChart
                  data={case_files.monthly_trend}
                  valueKey="completed"
                  labelKey="label"
                  color="#7C3AED"
                />
                <div className="text-muted" style={{ fontSize: '10px', textAlign: 'right', marginTop: 2 }}>
                  Cerrados · últimos 6 meses
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Fila 2: Trámites por tipo + Citas de la semana ── */}
      <div className="row mb-4">

        {/* Trámites por tipo */}
        <div className="col-12 col-md-5 mb-3">
          <div className="card h-100">
            <div className="card-header">
              <h6 className="mb-0">Trámites por tipo — este mes</h6>
            </div>
            <div className="card-body">
              {procedure_stats.length === 0 ? (
                <div className="text-center text-muted py-3">Sin trámites este mes</div>
              ) : (
                <>
                  {procedure_stats.map((p, i) => {
                    const total = procedure_stats.reduce((a, b) => a + b.count, 0) || 1;
                    const pct   = Math.round((p.count / total) * 100);
                    const colors = ['#5066E1', '#10B981', '#F59E0B', '#EF4444', '#7C3AED', '#06B6D4'];
                    return (
                      <div key={i} className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="font-weight-bold"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                            {p.name}
                          </small>
                          <small><strong>{p.count}</strong> <span className="text-muted">({pct}%)</span></small>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                          <div className="progress-bar"
                            style={{ width: `${pct}%`, backgroundColor: colors[i % colors.length] }}>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-top pt-2 mt-1 d-flex justify-content-between">
                    <small className="text-muted">Total trámites abiertos este mes</small>
                    <strong>{procedure_stats.reduce((a, b) => a + b.count, 0)}</strong>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Citas de la semana */}
        <div className="col-12 col-md-7 mb-3">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Citas de la semana</h6>
              <Link to="/agenda" className="btn btn-sm btn-outline-primary">Ver agenda</Link>
            </div>
            <div className="card-body p-0">
              {!week_appointments?.length ? (
                <div className="text-center text-muted py-4">No hay citas esta semana</div>
              ) : (
                <table className="table table-hover table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Fecha / Hora</th>
                      <th>Cita</th>
                      <th className="mobile-hide">Cliente</th>
                      <th className="mobile-hide">Responsable</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {week_appointments.map(a => {
                      const dt = new Date(a.start_datetime);
                      return (
                        <tr key={a.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div className="small font-weight-bold">
                              {dt.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}
                            </div>
                            <div className="small text-muted">
                              {dt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                            </div>
                          </td>
                          <td>
                            <Link to={`/agenda/${a.id}`} className="small font-weight-bold text-dark">
                              {a.title}
                            </Link>
                          </td>
                          <td className="mobile-hide">
                            <small>{a.client?.name ?? '—'}</small>
                          </td>
                          <td className="mobile-hide">
                            <small>{a.user?.name ?? '—'}</small>
                          </td>
                          <td>
                            <span className="badge badge-secondary" style={{
                              backgroundColor: appointmentStatusColors[a.status] ?? '#6b7280',
                              fontSize: '11px',
                            }}>
                              {a.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ── Fila 3: Carga de trabajo ── */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Carga de trabajo por empleado</h6>
          <Link to="/case-files" className="btn btn-sm btn-outline-secondary">Ver expedientes</Link>
        </div>
        <div className="card-body p-0">
          {workload.length === 0 ? (
            <div className="text-center text-muted py-4">Sin datos de carga</div>
          ) : (
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th className="mobile-hide">Rol</th>
                  <th>Activos</th>
                  <th>Sin actividad</th>
                  <th className="mobile-hide">Completados</th>
                  <th>Carga relativa</th>
                </tr>
              </thead>
              <tbody>
                {workload.map(u => {
                  const pct = Math.round((u.active / maxWorkload) * 100);
                  const hasStale = u.stale > 0;
                  return (
                    <tr key={u.id}>
                      <td className="td-name">
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: '50%',
                            backgroundColor: '#EEF0FB', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontWeight: 'bold', color: '#5066E1', fontSize: 13, flexShrink: 0,
                          }}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <Link to={`/case-files?assigned_user_id=${u.id}`} className="font-weight-bold">
                            {u.name}
                          </Link>
                        </div>
                        <div className="d-md-none mt-1">
                          <span className={`badge ${roleColors[u.role]}`}>{roleLabels[u.role]}</span>
                        </div>
                      </td>
                      <td className="mobile-hide">
                        <span className={`badge ${roleColors[u.role]}`}>{roleLabels[u.role]}</span>
                      </td>
                      <td>
                        <span className="badge badge-warning">{u.active}</span>
                      </td>
                      <td>
                        {hasStale
                          ? <span className="badge badge-danger">⚠️ {u.stale}</span>
                          : <span className="text-muted small">—</span>}
                      </td>
                      <td className="mobile-hide">
                        <span className="badge badge-success">{u.completed}</span>
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <div className="progress flex-grow-1" style={{ height: 8 }}>
                            <div className="progress-bar" style={{
                              width: `${pct}%`,
                              backgroundColor: hasStale ? '#EF4444' : '#5066E1',
                            }} />
                          </div>
                          <small className="text-muted">{u.active}</small>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

    </div>
  );
};

export default NotaryDashboard;